import { z } from 'zod';

import {
  CompanyReportSettings,
  CompanyReportType,
  GSEName,
  InvestorReportingReportType,
  InvestorReportingReportTypeToInvestor,
} from '@willow/types-iso';

export type InvestorReportingGseSettingKey =
  | 'investorReportingFnma'
  | 'investorReportingFhlmc'
  | 'investorReportingGnma'
  | 'investorReportingFha';

export const INVESTOR_REPORTING_GSE_SETTING_KEYS: Record<Exclude<GSEName, 'MI'>, InvestorReportingGseSettingKey> = {
  FNMA: 'investorReportingFnma',
  FHLMC: 'investorReportingFhlmc',
  GNMA: 'investorReportingGnma',
  FHA: 'investorReportingFha',
};

type InvestorReportingToggles = Partial<
  Pick<CompanyReportSettings, 'investorReporting' | InvestorReportingGseSettingKey>
>;

export const isInvestorReportingEnabledForGse = (
  reportSettings: InvestorReportingToggles | null | undefined,
  gse: GSEName,
): boolean => {
  // MI has no toggle. ADR and MILAR schedules are user created, one row per insurer, behind the
  // WHEDA admin flag, and investor reporting has never governed them. Gating them here would
  // silently stop reporting for companies that run them with the master toggle off.
  if (gse === 'MI') {
    return true;
  }

  return (
    reportSettings?.investorReporting === true && reportSettings[INVESTOR_REPORTING_GSE_SETTING_KEYS[gse]] !== false
  );
};

export const getInvestorReportTypesForGse = (gse: GSEName): InvestorReportingReportType[] =>
  InvestorReportingReportType.options.filter((type) => InvestorReportingReportTypeToInvestor[type] === gse);

const isInvestorReportingReportType = (type: CompanyReportType): type is InvestorReportingReportType =>
  z.validate(InvestorReportingReportType, type);

export const getGseForCompanyReportType = (type: CompanyReportType): GSEName | undefined =>
  isInvestorReportingReportType(type) ? InvestorReportingReportTypeToInvestor[type] : undefined;

export const isInvestorReportingEnabledForReportType = (
  reportSettings: InvestorReportingToggles | null | undefined,
  type: CompanyReportType,
): boolean => {
  const gse = getGseForCompanyReportType(type);
  return gse === undefined ? true : isInvestorReportingEnabledForGse(reportSettings, gse);
};
